/**
 * Global toast system (P1-T11). Reuses the `.toast` styles from tokens.css.
 * A single container is appended to <body>; toasts stack and auto-dismiss.
 */
import { ApiError } from '../api/client.js';

let container: HTMLElement | null = null;

function ensureContainer(): HTMLElement {
  if (!container) {
    container = document.createElement('div');
    container.id = 'toasts';
    container.setAttribute('aria-live', 'polite');
    document.body.appendChild(container);
  }
  return container;
}

/** Show a transient message. */
export function toast(message: string, durationMs = 2600): void {
  const host = ensureContainer();
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = message;
  host.appendChild(el);
  // Next frame so the enter transition plays.
  requestAnimationFrame(() => {
    el.classList.add('show');
  });
  window.setTimeout(() => {
    el.classList.remove('show');
    window.setTimeout(() => el.remove(), 220);
  }, durationMs);
}

/** Surface an API/network failure as a toast with a useful message. */
export function toastApiError(err: unknown, fallback = 'Something went wrong. Please try again.'): void {
  if (err instanceof ApiError) {
    toast(err.message || fallback);
  } else if (err instanceof TypeError) {
    // fetch() throws TypeError on network failure.
    toast('No connection. Check your network and try again.');
  } else if (err instanceof Error && err.message) {
    toast(err.message);
  } else {
    toast(fallback);
  }
}
